import React, {useEffect, useState} from "react";
import {useIntl} from "react-intl";
import * as Yup from "yup";
import {FormikProps, useFormik} from "formik";
import md5 from "md5";
import {Auth, FormInput, Icon, notify} from "@housebets/housebets-x-package";
import {CodeInput} from '@housebets/housebets-x-package/lib/components/form/CodeInput';

import {TFormikConfigStep1, TFormikConfigStep2} from "./types";
import apiUser from "app/api/user";
import {getPasswordSchema} from "app/utils/schemaValidation";

export const ForgotPassword: React.FC = () => {
    const {$t} = useIntl();
    const [activeStep, setStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [timerResendCode, setTimerResendCode] = useState(30);

    const formSchemaStep1 = () =>
        Yup.object().shape({
            email: Yup.string()
                .email($t({id: "InvalidEmail"}))
                .required($t({id: "FieldIsRequired"})),
        });

    const formSchemaStep2 = () =>
        Yup.object().shape({
            pin: Yup.string().nullable(),
            newPassword: getPasswordSchema($t),
            confirmPassword: Yup.string()
                .required($t({id: "FieldIsRequired"}))
                .oneOf([Yup.ref("newPassword")], $t({id: "PasswordMustMatch"})),
        });

    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (timerResendCode > 0) {
            interval = setInterval(() => {
                setTimerResendCode((prev) => prev - 1);
            }, 1000);
        }

        return () => clearInterval(interval);
    }, [timerResendCode]);

    const handleResendCode = async (email: string) => {
        try {
            setIsLoading(true);
            const res = await apiUser.getVerificationCode("email", email, "RESET_PASSWORD");
            const {status, data} = res;

            if (status === 200 && data.code === 0) {
                setStep(1);
                setTimerResendCode(30);
                return;
            }

            if (data.code === 101) {
                notify({
                    type: "error",
                    title: $t({id: "SystemError"}),
                });
                return;
            }
            notify({
                type: "error",
                title: data.code ? $t({id: `VerifyPhoneFail${data.code}`}) : data.message,
            });
        } catch (er) {
            // error
        } finally {
            setIsLoading(false);
        }
    };

    const formikStep1: FormikProps<TFormikConfigStep1> = useFormik<TFormikConfigStep1>({
        initialValues: {
            email: "",
        },
        validationSchema: formSchemaStep1(),
        validateOnBlur: true,
        onSubmit: async (payload) => {
            const {email} = payload;
            handleResendCode(email);
        },
    });

    const formikStep2: FormikProps<TFormikConfigStep2> = useFormik<TFormikConfigStep2>({
        initialValues: {
            pin: "",
            newPassword: "",
            confirmPassword: "",
        },
        validationSchema: formSchemaStep2(),
        validateOnBlur: true,
        onSubmit: async (payload) => {
            const {pin, newPassword} = payload;
            try {
                setIsLoading(true);
                const res = await apiUser.forgetPassword("email", formikStep1.values.email, pin, md5(newPassword));
                const {status, data} = res;

                if (status === 200 && data.code === 0) {
                    formikStep1.setValues({
                        email: "",
                    });
                    formikStep1.setErrors({});
                    formikStep1.setTouched({});
                    formikStep2.setValues({
                        pin: "",
                        newPassword: "",
                        confirmPassword: "",
                    });
                    formikStep2.setErrors({});
                    formikStep2.setTouched({});
                    setStep(2);
                    return;
                }

                if (data.code === 101) {
                    notify({
                        type: "error",
                        title: $t({id: "SystemError"}),
                    });
                    return;
                }
                notify({
                    type: "error",
                    title: data.code ? $t({id: `forgetPwCode_${data.code}`}) : data.message,
                });
            } catch (er) {
                // error
            } finally {
                setIsLoading(false);
            }
        },
    });

    const stepsContainer: Record<number, React.ReactNode> = {
        0: (
            <form autoComplete="off" noValidate onSubmit={formikStep1.handleSubmit}>
                <Auth.Form.Row>
                    <Auth.Form.FormTitle>{$t({id: "ForgotPassword.step2.title"})}</Auth.Form.FormTitle>
                    <Auth.Form.FormSubTitle>{$t({id: "ForgotPassword.step2.subtitle"})}</Auth.Form.FormSubTitle>
                </Auth.Form.Row>
                <Auth.Form.Row gap>
                    <FormInput
                        placeholder={$t({id: "EnterEmail"})}
                        disabled={isLoading}
                        name="email"
                        type="email"
                        label={$t({id: "EmailAddress"})}
                        required
                        size="sm"
                        value={formikStep1.values?.email}
                        errors={
                            !!formikStep1.errors?.email && !!formikStep1.touched?.email
                                ? {
                                      message: formikStep1.errors?.email,
                                  }
                                : null
                        }
                        onChange={(val) => formikStep1.setFieldValue("email", val)}
                        onFocus={() => formikStep1.setFieldTouched("email", false)}
                        onBlur={() => formikStep1.setFieldTouched("email", true)}
                    />
                </Auth.Form.Row>
                <Auth.Form.Row>
                    <Auth.Form.Button disabled={!("email" in formikStep1.touched) || isLoading} marginTopForgotPassword type="submit" text={$t({id: 'Sendcode'})} />
                </Auth.Form.Row>
            </form>
        ),
        1: (
            <form autoComplete="off" noValidate onSubmit={formikStep2.handleSubmit}>
                <Auth.Form.Row>
                    <Auth.Form.FormTitle>{$t({id: "ForgotPassword.step2.title"})}</Auth.Form.FormTitle>
                    <Auth.Form.FormSubTitle>{$t({id: "ForgotPassword.step2.subtitle"})}</Auth.Form.FormSubTitle>
                </Auth.Form.Row>
                <Auth.Form.Row gap>
                    <CodeInput
                        label=""
                        name="pin"
                        inputMode="numeric"
                        type="text"
                        disabled={isLoading}
                        fields={6}
                        value={formikStep2.values.pin}
                        error={
                            formikStep2?.errors?.pin
                                ? {
                                      message: "",
                                  }
                                : null
                        }
                        touch={() =>
                            formikStep2.setTouched({
                                pin: true,
                            })
                        }
                        untouch={() =>
                            formikStep2.setTouched({
                                pin: false,
                            })
                        }
                        onChange={(v) => {
                            formikStep2.setValues({
                                pin: v,
                                newPassword: formikStep2.values.newPassword,
                                confirmPassword: formikStep2.values.confirmPassword,
                            });

                            formikStep2.setTouched({
                                pin: true,
                            });
                        }}
                    />
                    <Auth.Form.FormSubTitle version="v2">
                        <Icon iconName="WarningIconV2" />
                        {$t({id: "ForgotPassword.warning"})}
                    </Auth.Form.FormSubTitle>
                </Auth.Form.Row>
                <Auth.Form.Row gap>
                    <FormInput
                        placeholder={$t({id: "EnterNewPassword"})}
                        name="password"
                        type="password"
                        label={$t({id: "ForgotPassword.confirmPassword"})}
                        required
                        size="sm"
                        disabled={isLoading}
                        value={formikStep2.values?.newPassword}
                        errors={
                            formikStep2.errors?.newPassword
                                ? {
                                      message: formikStep2.errors?.newPassword,
                                  }
                                : null
                        }
                        onChange={(val) => formikStep2.setFieldValue("newPassword", val)}
                        onFocus={() => formikStep2.setFieldTouched("newPassword", false)}
                        onBlur={() => formikStep2.setFieldTouched("newPassword", true)}
                    />
                </Auth.Form.Row>
                <Auth.Form.Row gap>
                    <FormInput
                        placeholder={$t({id: "EnterNewPassword"})}
                        name="password"
                        type="password"
                        label={$t({id: "ConfirmPassword"})}
                        required
                        size="sm"
                        disabled={isLoading}
                        value={formikStep2.values?.confirmPassword}
                        errors={
                            !!formikStep2.errors?.confirmPassword && !!formikStep2.touched?.confirmPassword
                                ? {
                                      message: formikStep2.errors?.confirmPassword,
                                  }
                                : null
                        }
                        onChange={(val) => formikStep2.setFieldValue("confirmPassword", val)}
                        onFocus={() => formikStep2.setFieldTouched("confirmPassword", false)}
                        onBlur={() => formikStep2.setFieldTouched("confirmPassword", true)}
                    />
                </Auth.Form.Row>
                <Auth.Form.Row relative>
                    <Auth.Form.Button
                        disabled={!!formikStep2.errors?.confirmPassword || !!formikStep2.errors?.newPassword || !!formikStep2?.errors?.pin || isLoading}
                        marginTopForgotPassword
                        type="submit"
                        text="Sumbit"
                    />
                    <Auth.Form.FormSubTitle version="v3">
                        {$t({id: "ForgotPassword.resendPrompt"})}{" "}
                        <Auth.Form.ForgotPassword
                            disabled={timerResendCode !== 0 || !!formikStep2.errors?.confirmPassword || !!formikStep2.errors?.newPassword}
                            version="v2"
                            onClick={() => handleResendCode(formikStep1.values.email)}
                            text={$t({id: "Resend"}) + ' (' + timerResendCode + ')'}
                        />
                    </Auth.Form.FormSubTitle>
                </Auth.Form.Row>
            </form>
        ),
        2: (
            <div>
                <Auth.Form.AnimationWrapper />
                <Auth.Form.FormTitle>{$t({id: "password.success"})}</Auth.Form.FormTitle>
            </div>
        ),
    };

    return <Auth.Form.Wrapper>{stepsContainer[activeStep]}</Auth.Form.Wrapper>;
};

ForgotPassword.displayName = "Auth.ForgotPassword";
